import { Table, tableFromIPC } from 'apache-arrow';

import {
    EventType,
    InterfaceVariablesType,
    OffsetType,
    PilotagePlanSettingType,
    PlanType,
    PortShipType,
    PortType,
    PositionType,
    SwitchType,
    TagType,
    TrackType,
    TransitDataType,
    TransitOptionType,
    TransitsDataType,
    TransitType,
    VesselCategoryType,
    VesselType,
} from 'types';
import { FilterObjectType } from 'types/search';
import { ToolTipType } from './../types/index.d';

import { MOVING_VESSEL_THRESHOLD } from 'data/static';
import { downloadDataFile } from 'services/transits';
import { PortState } from 'store/reducers/portReducer';
import { dateTimeFormat, DateTimeFormat } from './dateTimeFormat';
import { clone, getId, setKey } from './formHandlers';
import { QueryBuilder, SearchField } from './queryBuilder';

export function formatFilterObject(filterObject: FilterObjectType, version = 1, portData: PortState) {
    const getVesselType = (types: number[], key: 'id' | 'name' = 'name') =>
        types.map((id) => portData?.vesselCategories.find((v) => v.id === id)?.[key]).filter(Boolean);

    const getTag = (tags: number[], tagType: string[], key = 'key') =>
        tags.map((id) => portData?.tags.find((t) => t.id === id && tagType.includes(t.type))?.[key]).filter(Boolean);

    const getSource = (id: string, key: 'id' | 'name' | 'key' = 'id') =>
        portData.port?.port_urls.find((x) => x.id === Number(id.replace('_', '')) && x.type === 'environmental')?.[key];

    const hasRange = (range?: { gte?: number; lte?: number }) => range?.gte !== undefined || range?.lte !== undefined;

    const filterValues = (obj) => {
        return {
            gte: obj.gte,
            lte: obj.lte,
        };
    };

    /* -------------------- V2 Query Builder -------------------- */
    if (version === 2) {
        const builder = new QueryBuilder();
        const conditions: any[] = [];

        /* -------- Date -------- */
        const { start_utc, end_utc } = filterObject.date;

        if (start_utc?.op || end_utc?.op) {
            const range: { gte?: string; lte?: string } = {};

            if (start_utc?.op) {
                range.gte = start_utc.op.toIso(true);
            }

            if (end_utc?.op) {
                range.lte = end_utc.op.toIso(true);
            }

            conditions.push(builder.range(SearchField.TRANSIT_START_TIME_UTC, range));
        }

        /* -------- Vessel -------- */
        const vessel = filterObject.vessel;

        if (vessel.name?.icontains) {
            conditions.push(
                builder.wildcard(
                    SearchField.VESSEL_NAME,
                    `*${vessel.name.icontains.trim().replace(/\s+/g, '*').toLowerCase()}*`
                )
            );
        }

        if (hasRange(vessel.loa_m) && (!vessel.loa_m.max || !vessel.loa_m.min)) {
            conditions.push(builder.range(SearchField.VESSEL_LOA_M, filterValues(vessel.loa_m)));
        }

        if (hasRange(vessel.beam_m) && (!vessel.beam_m.max || !vessel.beam_m.min)) {
            conditions.push(builder.range(SearchField.VESSEL_BEAM_M, filterValues(vessel.beam_m)));
        }

        if (filterObject.vesselCategories?.length) {
            //@ts-ignore
            conditions.push(builder.terms(SearchField.VESSEL_TYPE, getVesselType(filterObject.vesselCategories)));
        }
        /* -------- Tags -------- */
        if (filterObject.tags?.length) {
            const checkTags = [
                { key: SearchField.TAGS_SYSTEM, value: ['system','admin'] },
                { key: SearchField.TAGS_TYPE, value: ['type'] },
                { key: SearchField.TAGS_USER, value: ['public', 'private'] },
                { key: SearchField.TAGS_ANALYSIS, value: ['analysis'] },
                { key: SearchField.TAGS_POSITIONING, value: ['positioning'] },
            ];

            checkTags.forEach((t) => {
                const v = getTag(filterObject.tags, t.value);
                if (v.length) {
                    conditions.push(builder.terms(t.key, v));
                }
            });
        }

        if (filterObject.withComments) {
            conditions.push(builder.exists(SearchField.COMMENTS));
        }

        /* -------- Metocean Generic Builder -------- */

        const buildMetoceanOR = (source: any, buildConditions: (value: any) => any[]) => {
            const orConditions = Object.entries(source)
                .filter(([, v]: any) => v.status)
                .map(([key, value]: any) =>
                    builder.and_condition([
                        builder.term(SearchField.METOCEAN_REGION, 'Whole Port'),
                        builder.term(SearchField.METOCEAN_SLUG, getSource(key, 'key')),
                        ...buildConditions(value),
                    ])
                );

            if (orConditions.length) {
                conditions.push(builder.or_condition(orConditions));
            }
        };

        /* -------- Tide -------- */
        if (filterObject.tide) {
            buildMetoceanOR(filterObject.tide, (v) => [
                builder.range(SearchField.METOCEAN_HEIGHT_M, filterValues(v.height_m)),
            ]);
        }

        /* -------- Wind -------- */
        if (filterObject.wind) {
            buildMetoceanOR(filterObject.wind, (v) => [
                builder.range(SearchField.METOCEAN_GUST_KN, filterValues(v.gust_kn)),
                builder.range(SearchField.METOCEAN_SPEED_KN, filterValues(v.speed_kn)),
                builder.or_condition(
                    v?.direction_from_deg?.map((d: any) =>
                        builder.range(SearchField.METOCEAN_DIRECTION_DEG, filterValues(d))
                    ) || []
                ),
            ]);
        }

        /* -------- Water Current -------- */
        if (filterObject.watercurrent) {
            buildMetoceanOR(filterObject.watercurrent, (v) => [
                builder.range(SearchField.METOCEAN_SPEED_KN, filterValues(v.speed_kn)),
                builder.or_condition(
                    v?.direction_to_deg?.map((d: any) =>
                        builder.range(SearchField.METOCEAN_DIRECTION_DEG, filterValues(d))
                    ) || []
                ),
            ]);
        }

        /* -------- Wave -------- */
        if (filterObject.wave) {
            buildMetoceanOR(filterObject.wave, (v) => [
                builder.range(SearchField.METOCEAN_TP_SEA_S, filterValues(v.mean_wave_period_sea_s)),
                builder.range(SearchField.METOCEAN_TP_SWELL_S, filterValues(v.mean_wave_period_swell_s)),
                builder.range(SearchField.METOCEAN_TP_TOTAL_S, filterValues(v.mean_wave_period_total_s)),
                builder.range(SearchField.METOCEAN_TM_SWELL_S, filterValues(v.peak_wave_period_swell_s)),
                builder.range(SearchField.METOCEAN_TM_TOTAL_S, filterValues(v.peak_wave_period_total_s)),
                builder.range(SearchField.METOCEAN_HS_SEA_M, filterValues(v.significant_wave_height_sea_m)),
                builder.range(SearchField.METOCEAN_HS_SWELL_M, filterValues(v.significant_wave_height_swell_m)),
                builder.range(SearchField.METOCEAN_HS_TOTAL_M, filterValues(v.significant_wave_height_total_m)),
                builder.or_condition(
                    v?.mean_direction_from_deg?.map((d: any) =>
                        builder.range(SearchField.METOCEAN_DIRECTION_DEG, filterValues(d))
                    ) || []
                ),
            ]);
        }

        const query = builder.and_condition(conditions);
        return query;
    }

    /* -------------------- API Version Cleanup -------------------- */

    const apiVersion: FilterObjectType = clone(filterObject, true);

    if (apiVersion.date.end_utc.op === null) {
        setKey(apiVersion, 'date.end_utc', {});
    }
    if (apiVersion.date.start_utc.op === null) {
        setKey(apiVersion, 'date.start_utc', {});
    }

    setKey(apiVersion, 'text', filterObject.withComments ? ['with_user_comments'] : []);

    return apiVersion;
}

export function flatTrackDate(dateTimeFormat: DateTimeFormat, data: any): PositionType[] {
    return Object.keys(data).map((x: string) => {
        return {
            ...data[x],
            valid_time_utc: dateTimeFormat.parseDateTime(data[x].valid_time_utc),
        };
    });
}

export function getSourceDefaultData(defaultData: any, source: any, version: number | undefined, attr = null) {
    let data = {};
    if (version === 2) {
        data = defaultData?.[source.environmental_type]?.[source.key];
    } else {
        data = defaultData?.[source.environmental_type]?.[`_${source.id}`];
    }
    if (attr) {
        return data[attr];
    }
    return data;
}

export function fromatDefaultData(data: any, port: PortType) {
    if (port.backEnd?.version === 2) {
        const createRanges = (data: any) => {
            return data.all.map((x: string) => {
                const [gte, lte] = x.split('_').map((v) => parseInt(v, 10));

                return {
                    index: Math.floor(gte / 45) + 1,
                    gte,
                    lte,
                };
            });
        };

        const findSourceByType = (data: any, type: string) => {
            const sources: any = {};
            Object.keys(data).forEach((sourceSlug) => {
                if (sourceSlug.includes(`-${type}-`)) {
                    const portUrl = port.port_urls.find((s) => {
                        return s.key === sourceSlug;
                    });

                    if (portUrl) {
                        const sourceData = data[sourceSlug];

                        sources[`_${portUrl.id}`] = {
                            date: [],
                        };

                        Object.keys(sourceData).forEach((pram) => {
                            const pramData = data[sourceSlug][pram];

                            if (pram.includes('_deg')) {
                                switch (type) {
                                    case 'watercurrent':
                                        sources[`_${portUrl.id}`]['direction_to_deg'] = createRanges(pramData);
                                        break;
                                    case 'wind':
                                        sources[`_${portUrl.id}`]['direction_from_deg'] = createRanges(pramData);
                                        break;
                                    case 'wave':
                                        sources[`_${portUrl.id}`]['mean_direction_from_deg'] = createRanges(pramData);
                                        break;

                                    default:
                                        break;
                                }
                            } else if (pram === 'end_time_utc') {
                                sources[`_${portUrl.id}`]['date'][1] = pramData['max'];
                            } else if (pram === 'start_time_utc') {
                                sources[`_${portUrl.id}`]['date'][0] = pramData['min'];
                            } else {
                                const linkPramName = linkEnvoKeys(pram, true);
                                sources[`_${portUrl.id}`][linkPramName] = {
                                    status: true,
                                    min: true,
                                    lte: toFixedValue(
                                        pramData.max,
                                        port.meta_data.enviroDisplay[type][linkPramName].decimal
                                    ),
                                    max: true,
                                    gte: toFixedValue(
                                        pramData.min,
                                        port.meta_data.enviroDisplay[type][linkPramName].decimal
                                    ),
                                };
                            }
                        });
                    }
                }
            });

            return sources;
        };

        return {
            tags: [
                ...data.tags.all.map((x: string) => {
                    return { slug: x, total: 1 };
                }),
                ...data.transit.transit_type.all.map((x: string) => {
                    return { slug: x, total: 1 };
                }),
            ],

            transits: {
                draft_m: {
                    lte: data.vessel.draft_m.max,
                    min: true,
                    gte: data.vessel.draft_m.min,
                    max: true,
                },
            },
            vessel: {
                loa_m: {
                    lte: data.vessel.loa_m.max,
                    min: true,
                    gte: data.vessel.loa_m.min,
                    max: true,
                },
                beam_m: {
                    lte: data.vessel.beam_m.max,
                    min: true,
                    gte: data.vessel.beam_m.min,
                    max: true,
                },
            },
            tide: findSourceByType(data.enviro, 'tide'),
            directionalWave: findSourceByType(data.enviro, 'directionalWave'),
            vesselCategories: data.vessel.vessel_type.all,
            watercurrent: findSourceByType(data.enviro, 'watercurrent'),
            wave: findSourceByType(data.enviro, 'wave'),
            wind: findSourceByType(data.enviro, 'wind'),
        };
    }

    return data[port.id];
}

export function formatSystemData(data: any, version: number | undefined) {
    if (version === 1) {
        return data;
    }

    return {
        today: data.today,
        system: data.system,
        endDate: data.end_date_utc,
        startDate: data.start_date_utc,
        forReview: data.total_for_review,
        simulated: data.total_simulated,
        total: data.total,
        ppu: data.total_ppu,
        items: {
            ...data.last_transit,
            valid_time_utc: data.last_ais_utc,
        },
        environmental: {
            ...data.last_transit,
            valid_time_utc: data.last_transit.last_updated_time_utc,
        },
    };
}
export function formatSearchNumbers(data: any, version: number | undefined) {
    if (version !== 2) {
        return data;
    }

    return {
        dateRange: data.date_range_total + data.date_range_for_review_total,
        total: data.total + data.for_review,
        dateRangeForReview: data.date_range_for_review_total,
        forReview: data.for_review,
        ppu: 0,
        simulated: 0,
        startDate: data.from_utc,
        endDate: data.to_utc,
    };
}

export function updatePilotagePlanSettings(
    pilotagePlanSettings: PilotagePlanSettingType,
    plans: PlanType[]
): PilotagePlanSettingType {
    const updatedSettings: PilotagePlanSettingType = {};

    Object.keys(pilotagePlanSettings).forEach((settingKey) => {
        const setting = pilotagePlanSettings[settingKey];
        const { options } = setting;

        // Check if any of the options has a non-empty value in any plan
        const hasDataInPlans = options?.some((option) =>
            plans.some((plan) => {
                const value = plan[option as keyof PlanType];
                if (Array.isArray(value)) {
                    return value.length > 0 && value.some((v) => v !== '');
                }
                return value !== undefined && value !== null && value !== '';
            })
        );

        if (hasDataInPlans) {
            updatedSettings[settingKey] = setting;
        }
    });
    return updatedSettings;
}

export function isVesselMoving(data: any) {
    if (data) {
        const sogValues = Object.values(data)
            //@ts-ignore
            .map((v) => v.sog_mps)
            .filter((v) => typeof v === 'number');

        const averageSog = sogValues.reduce((sum, v) => sum + v, 0) / sogValues.length;

        if (averageSog >= MOVING_VESSEL_THRESHOLD) {
            return true;
        }
    }
    return false;
}

export function copyTracksOptions(tracks: TrackType[], options: any[]): TrackType[] {
    return tracks.map((track) => {
        const option = options.find((opt) => opt.name === track.options.group);

        if (!option || option.displayMoving === undefined || option.drawTrackNotMoving === undefined) return track;

        const isMoving = track.options.moving;

        return {
            ...track,
            options: {
                ...track.options,
                display: isMoving ? option.displayMoving : option.displayNotMoving,
            },
        };
    });
}

export function formatTracks(
    transitId: number | null,
    mmsi: number,
    sourceTracks: any,
    switchs: SwitchType,
    interfaceVariables: InterfaceVariablesType,
    minimumLength: number,
    portShips?: PortShipType[],
    transitShips?: PortShipType[],
    mode = 'search'
): TrackType[] {
    const tracks: TrackType[] = [];

    const getVesselGroup = (
        mmsi: number,
        vessel: VesselType,
        interfaceVariables: InterfaceVariablesType,
        minimumLength: number,
        portShips?: PortShipType[],
        transitShips?: PortShipType[],
        data?: any
    ) => {
        //small vessel
        const moving = isVesselMoving(data);
        if (mmsi === vessel.mmsi) {
            return { group: 'transitVessels', moving, transitId: transitId, status: true, drawTrack: true }; // this is the selected transit
        }
        if (
            portShips?.length &&
            portShips.find((x) => {
                return x.mmsi === vessel.mmsi;
            })
        ) {
            return {
                group: 'portVessels',
                moving,
                transitId: null,
                status: interfaceVariables.displayPortVessels_boolean,
                loading: interfaceVariables.displayPortVessels_boolean,
                drawTrack: false,
            };
        }

        if (vessel.loa_m < minimumLength) {
            return {
                group: 'otherVessels',
                moving,
                transitId: null,
                status: interfaceVariables.displayOtherVessels_boolean,
                loading: interfaceVariables.displayOtherVessels_boolean,
                drawTrack: false,
            };
        } else {
            //if this is a transitShips group
            if (transitShips?.length) {
                // if in it then it's otherTransit
                if (
                    transitShips.find((x) => {
                        return x.mmsi === vessel.mmsi;
                    })
                ) {
                    //if not then to the other vessel
                    return {
                        group: mode === 'search' ? 'otherTransits' : 'transitVessels',
                        moving,
                        transitId: null,
                        status: interfaceVariables.displayTransitVessels_boolean,
                        loading: interfaceVariables.displayTransitVessels_boolean,
                        drawTrack: false,
                    };
                }
                return {
                    group: 'otherVessels',
                    moving,
                    status: interfaceVariables.displayOtherVessels_boolean,
                    loading: interfaceVariables.displayOtherVessels_boolean,
                    drawTrack: false,
                };
            } else {
                return {
                    group: mode === 'search' ? 'otherTransits' : 'transitVessels',
                    moving,
                    transitId: null,
                    status: interfaceVariables.displayTransitVessels_boolean,
                    loading: interfaceVariables.displayTransitVessels_boolean,
                    drawTrack: false,
                };
            }
        }
    };

    Object.keys(sourceTracks).forEach((sourceTrack) => {
        const bestData = sourceTracks[sourceTrack].best;
        const data = sourceTracks[sourceTrack][bestData].data;
        const { group, transitId, status, drawTrack, moving } = getVesselGroup(
            mmsi,
            sourceTracks[sourceTrack].vessel,
            interfaceVariables,
            minimumLength,
            portShips,
            transitShips,
            data
        );

        tracks.push({
            uuid: getId(),

            mmsi: sourceTracks[sourceTrack].vessel.mmsi, // v2 map // same
            vessel_id: sourceTracks[sourceTrack].vessel.id,
            vessel: sourceTracks[sourceTrack].vessel,

            data: {
                events: [],
                offsets: [],
            },
            // static data
            //@ts-ignore
            //data: { ...trackData, offsets: tracks[track].offsets, [`${transit['best']}Id`]: trackId },
            // static options
            options: {
                group: group,
                moving: moving,
                display: status, //status
                loading: false,
                transitId: transitId,
                drawTrack: drawTrack,
                trackLoaded: false,

                rawAIS: switchs.rawAIS.value,
                centroid: switchs.centroid.value,
                smoothed: switchs.smoothed.value,

                rawAISLoaded: false,
                centroidLoaded: false,
                smoothedLoaded: false,

                vesselStatus: switchs.vesselStatus.value,
                systemTags: switchs.systemTags.value,
                routeTags: switchs.routeTags.value,
                debugTags: switchs.debugTags.value,
            },
        });
    });

    return tracks;
}

export function formatTooltips(toolTips: ToolTipType[], dateTimeFormat: dateTimeFormat): ToolTipType[] {
    return toolTips.map((x: ToolTipType) => {
        return { ...x, dataIndex: { ...x.dataIndex, time: dateTimeFormat.parseDateTime(x.dataIndex.time.isoDate) } };
    });
}

export function findBestData(
    data: any,
    afterTime?: DateTimeFormat | null,
    beforeTime?: DateTimeFormat | null,
    dataType?: string
) {
    const isWithinTimeRange = (itemTime, after, before) => {
        const timeStamp = itemTime.toTimestamp();
        const afterValid = after ? timeStamp >= after.toTimestamp() : true;
        const beforeValid = before ? timeStamp <= before.toTimestamp() : true;
        return afterValid && beforeValid;
    };

    const tryReturn = (sourceData, type, center = true) => {
        if (afterTime || beforeTime) {
            const validItems = sourceData.filter((item) =>
                //@ts-ignore
                isWithinTimeRange(item.valid_time_utc, afterTime, beforeTime)
            );

            if (validItems.length) return { center, data: validItems, type };
            else return null;
        }

        return { center, data: sourceData, type };
    };
    const rawAISresult = tryReturn(data?.rawAIS || [], 'rawAIS', false);
    if (dataType) {
        const result = tryReturn(data[dataType] || [], dataType, true);
        if (result) return { ...result, rawData: rawAISresult };
    } else {
        if (data?.smoothed?.length) {
            const result = tryReturn(data.smoothed || [], 'smoothed', true);

            if (result) return { ...result, rawData: rawAISresult };
        }
        if (data?.centroid?.length) {
            const result = tryReturn(data.centroid || [], 'centroid', true);
            if (result) return { ...result, rawData: rawAISresult };
        }

        if (rawAISresult) return { ...rawAISresult, rawData: rawAISresult };
    }

    // Fallback result
    return { center: false, data: data?.rawAIS || [], type: 'rawAIS', rawData: rawAISresult };
}

export function findBestPosition(data, type, rawData, currentTime: DateTimeFormat) {
    const position = data
        .filter(
            //@ts-ignore
            (item) => item.valid_time_utc.toTimestamp() <= currentTime?.toTimestamp()
        )
        .sort((a, b) => b.valid_time_utc.toTimestamp() - a.valid_time_utc.toTimestamp())[0];

    const rawPosition = rawData
        .filter(
            //@ts-ignore
            (item) => item.valid_time_utc.toTimestamp() <= currentTime?.toTimestamp()
        )
        .sort((a, b) => b.valid_time_utc.toTimestamp() - a.valid_time_utc.toTimestamp())[0];
    if (position?.valid_time_utc.toTimestamp() >= rawPosition?.valid_time_utc.toTimestamp()) {
        return { position, center: type === 'rawAIS' ? false : true };
    }
    return { position: rawPosition, center: false };
}

export function formatVesselFoundList(data: any, version: number | undefined) {
    if (version !== 2) {
        return data.items;
    }

    return data.vessels.map((x) => {
        return {
            id: null,
            type: 'vesselName',
            value: x.name,
        };
    });
}

export function formatMetoceanChart(data: any, version: number, config: any) {
    if (version !== 2) {
        return data.items;
    }

    const sourceFormat = config.enviroDisplay[config.type];

    return data.sources
        .filter((x) => {
            return x.enviro_type === config.type;
        })
        .map((x) => {
            const source = config.environmentalSources.find((y: any) => y.key === x.source_slug);
            return {
                data: x.time_utc.map((t, i) => {
                    const r = {
                        valid_time_utc: t,
                        id: i,
                    };

                    Object.keys(sourceFormat).forEach((s) => {
                        r[s] = x[linkKeys(s, source.environmental_type)]?.[i] || null;
                    });

                    return r;
                }),
                url: { id: x.source_id, description: source.description },
            };
        });
}

export function formatLiveAndTimeVesselsData(data: any, id: string | number, version: number | undefined) {
    if (version !== 2) {
        return data.items;
    }

    if (!data.vessels?.[0]) {
        return data;
    }
    const vesselData = data.vessels[0];
    const offsets: any = {};
    vesselData.offsets.forEach((x: any) => {
        offsets[x.id] = formatOffset(x);
    });

    const raw: any = [];

    vesselData.raw_positions.message_time_utc.map((x: any, i: string | number) => {
        raw.push({
            id: getId(),
            cog_deg: vesselData.raw_positions.cog_deg[i],
            east_utm: vesselData.raw_positions.easting_m[i],
            heading_deg: vesselData.raw_positions.heading_deg[i],
            north_utm: vesselData.raw_positions.northing_m[i],
            sog_mps: vesselData.raw_positions.sog_kn[i] * 0.514444,
            rot_deg_min: vesselData.raw_positions.rot[i],
            vessel_offset_id: vesselData.raw_positions.offset_id[i],
            transit_id: 0,
            navigational_status_id: 0,
            navigational_status: 'N/A',
            valid_time_utc: x,
        });
    });

    return {
        [`v-${id}`]: {
            uuid: vesselData.id,
            vessel_id: vesselData.id,
            offsets,
            rawAIS: {
                data: raw,
                coordinates: [],
                center: [],
            },
            centroid: {
                data: {},
                coordinates: [],
                center: [],
            },
            smoothed: {
                data: {},
                coordinates: [],
                center: [],
            },
        },
    };
}

export function setGroup(
    port: PortType,
    portShips: (string | number)[],
    transitShips: (string | number)[],
    vessel: any
): string {
    if (transitShips && transitShips.length > 0) {
        // if in whitelist
        if (transitShips.includes(vessel.mmsi)) {
            return 'otherTransit';
        } else {
            if (portShips.includes(vessel.mmsi)) {
                return 'portVessel';
            }
        }
    } else {
        if (vessel.loa_m >= port.minimum_length && !portShips.includes(vessel.mmsi)) {
            return 'otherTransit';
        } else {
            if (portShips.includes(vessel.mmsi)) {
                return 'portVessel';
            }
        }
    }

    return 'otherVessels';
}

export function formatLiveAndTimeTracksData(
    data: any,
    port: PortType,
    version: number | undefined,
    vesselCategories: VesselCategoryType[]
) {
    if (version !== 2) {
        return data.items;
    }

    return data.vessels.map((x: any) => {
        const other = vesselCategories.find((s) => {
            return s.name === 'Other';
        });
        const vesselCategory = vesselCategories.find((s) => {
            return s.name === x.vessel_type;
        });

        return {
            uuid: x.id,
            vessel_id: x.id,
            loading: true,
            best: 'rawAIS',
            ready: true,
            group: setGroup(
                port,
                port.port_ships.map((s) => {
                    return s.mmsi;
                }),
                port.transit_ships.map((s) => {
                    return s.mmsi;
                }),
                x
            ),
            sweptPath: false,
            selected: true,
            toLoad: false,
            track: false,
            outline: true,
            vessel: {
                id: x.id,
                mmsi: parseInt(x.mmsi),
                draft_m: null,

                track: 'False',
                imo: x.imo,
                name: x.shipname,
                callsign: x.callsign,
                loa_m: x.loa_m,
                beam_m: x.beam_m,

                vessel_sub_category: null,

                vessel_type: null,
                vessel_category: vesselCategory || other,
            },
            transits: [],
            offsets: {},
            rawAIS: {
                data: {},
                coordinates: [],
                center: [],
            },
            centroid: {
                data: {},
                coordinates: [],
                center: [],
            },
            smoothed: {
                data: {},
                coordinates: [],
                center: [],
            },
        };
    });
}

export function formatSavedFilter(dateTimeFormat: DateTimeFormat, filter: FilterObjectType) {
    //@ts-ignore
    if (filter.dateRange != undefined) {
        //@ts-ignore
        setKey(filter, 'date.dateRange', filter.dateRange || 'lastDays');
    }
    //@ts-ignore
    if (filter.lastDays != undefined) {
        //@ts-ignore
        setKey(filter, 'date.lastDays', Math.abs(filter.lastDays));
    }

    if (
        ((filter.date.lastDays === '' || filter.date.lastDays === 0) && filter.date.dateRange === '') ||
        filter.date.dateRange === 'activeDays'
    ) {
        setKey(
            filter,
            'date.start_utc.op',
            filter.date.start_utc?.op === null ? null : dateTimeFormat.parseDateTime(filter.date.start_utc?.op?.date)
        );
        setKey(
            filter,
            'date.end_utc.op',
            filter.date.end_utc?.op === null ? null : dateTimeFormat.parseDateTime(filter.date.end_utc?.op?.date)
        );
    } else {
        const { startDate, endDate } = dateTimeFormat
            .clone()

            .getDateFromDays(
                filter.date.lastDays === 0 && filter.date.dateRange === '' ? 'activeToday' : filter.date.dateRange,
                //@ts-ignore
                filter.date.lastDays
            );

        setKey(filter, 'date.end_utc.op', endDate);
        setKey(filter, 'date.start_utc.op', startDate);
    }
    //@ts-ignore
    setKey(filter, 'date.lastDays', filter.date.lastDays === '' ? '' : Math.abs(filter.date.lastDays));
    setKey(
        filter,
        'date.dateRange',
        filter.date.lastDays === 0 && filter.date.dateRange === '' ? 'activeToday' : filter.date.dateRange
    );
    return filter;
}

export function replaceOptions(transits: TransitType[], options: TransitOptionType[]): TransitType[] {
    return transits.map((transit: TransitType) => {
        const option = options.find((x) => {
            //@ts-ignore
            return x.id === transit.id;
        });
        if (!option) {
            return transit;
        }
        return {
            ...transit,
            options: {
                ...transit.options,
                display: option.display,
                highlight: option.highlight,
                inspect: option.inspect,
                outline: option.inspect ? false : option.outline,
                oldOutline: option.oldOutline,
                oldDrawTrack: option.oldDrawTrack,
                drawTrack: option.inspect ? false : option.drawTrack,
                drawSweptPath: option.drawSweptPath,
                select: option.select,
            },
        };
    });
}

export function formatComments(data: any, version: number) {
    if (version !== 2) {
        return data.items;
    }

    return data.items;
}

export function formatTransitTags(data: any, version: number) {
    if (version !== 2) {
        return data.items;
    }

    return data.tags.map((x) => {
        return { tag_id: x.slug };
    });
}

export function linkTagType(type: string) {
    const TypeMap: Record<string, string> = {
        public: 'user',
        private: 'user',
        type: 'transit_type',
    };

    // Return mapped key if exists, otherwise return the original key
    return TypeMap[type] || type;
}

export function linkKeys(key: string, type: string, reverse = false) {
    const keyMaps: Record<string, Record<string, string>> = {
        wave: {
            mean_direction_from_deg: 'direction_mean_deg',
            mean_wave_period_sea_s: 'tm01_s_SEA',
            mean_wave_period_swell_s: 'tm01_s_SWELL',
            mean_wave_period_total_s: 'tm01_s_TOTAL',
            peak_wave_period_swell_s: 'tp_s_SWELL',
            peak_wave_period_total_s: 'tp_s_TOTAL',
            peak_wave_period_sea_s: 'tp_s_SEA',
            significant_wave_height_sea_m: 'hs_m_SEA',
            significant_wave_height_swell_m: 'hs_m_SWELL',
            significant_wave_height_total_m: 'hs_m_TOTAL',
        },
        wind: {
            // direction_from_deg: 'direction_deg_tn_to',
        },
        watercurrent: {
            direction_to_deg: 'direction_deg_tn_to',
        },
        maneuver: {
            rot_deg_min: 'rot',
            heading_deg: 'heading_deg',
            draft_m: 'draft_m',
            cog_deg: 'cog_deg',
            crot_deg_min: 'crot_deg_min',
            sog_mps: 'sog_kn',
            startTransit: 'start_transit',
            endTransit: 'end_transit',
            vesselStatusUpdate: 'vessel_state_change',
            enterPilotBoardingGround: 'enter_pilotboarding_areas',
            exitPilotBoardingGround: 'exit_pilotboarding_areas',
            enterRegion: 'enter_region_areas',
            exitRegion: 'exit_region_areas',
        },
    };

    const keyMap = keyMaps[type] || {};

    if (!reverse) {
        return keyMap[key] || key;
    }

    const reverseKeyMap = Object.fromEntries(Object.entries(keyMap).map(([k, v]) => [v, k]));

    return reverseKeyMap[key] || key;
}

export function linkEnvoKeys(key: string, reverse = false) {
    const keyMap: Record<string, string> = {
        mean_wave_period_sea_s: 'tp_sea_s',
        mean_wave_period_swell_s: 'tp_swell_s',
        mean_wave_period_total_s: 'tp_total_s',

        peak_wave_period_swell_s: 'tm_swell_s',
        peak_wave_period_total_s: 'tm_total_s',
        peak_wave_period_sea_s: 'tm_sea_s',

        significant_wave_height_sea_m: 'hs_sea_m',
        significant_wave_height_swell_m: 'hs_swell_m',
        significant_wave_height_total_m: 'hs_total_m',
    };

    // Return mapped key if exists, otherwise return the original key
    if (!reverse) {
        return keyMap[key] || key;
    }

    const reverseKeyMap: Record<string, string> = Object.fromEntries(Object.entries(keyMap).map(([k, v]) => [v, k]));

    return reverseKeyMap[key] || key;
}

export function formatMetOceanData(data: any, version: number, config: any, enviroDisplay: any) {
    if (version !== 2) {
        return data;
    }

    const getValues = (instrument, key, data) => {
        const sourceData = data.find((x) => {
            return x.enviro_type === key && x.source_slug === instrument.slug;
        }); // for now until we link the instrument id

        return Object.keys(enviroDisplay[key]).map((x, i) => {
            const e = enviroDisplay[key][x];

            return {
                index: i,
                name: e.name,
                key: x,
                value: sourceData?.[linkKeys(x, key)]?.[0] || null,
                unit: e.unit,
            };
        });
    };

    const results = config.map((type) => {
        return {
            key: type.key,
            instruments: type.instruments.map((instrument) => {
                return {
                    ...instrument,
                    values: getValues(instrument, type.key, data),
                };
            }),
        };
    });

    return results;
}

export function unifiedEvents(events: any[], transitId: string): EventType[] {
    const eventConfig: Record<
        string,
        {
            type: EventType['type'];
            description: (event: any) => string;
            algorithm?: string;
        }
    > = {
        enter_pilotboarding_areas: {
            type: 'enterPilotBoardingGround',
            description: (x) => `vessel enter ${x.data.area}`,
        },

        exit_pilotboarding_areas: {
            type: 'exitPilotBoardingGround',
            description: (x) => `vessel exit ${x.data.area}`,
        },

        enter_backmanoeuvre_areas: {
            type: 'enterSwing',
            description: (x) => `vessel enter swing \`${x.data.area}\``,
        },

        exit_backmanoeuvre_areas: {
            type: 'exitSwing',
            description: (x) => `vessel exit swing \`${x.data.area}\``,
        },

        enter_region_areas: {
            type: 'enterRegion',
            description: (x) => `vessel enter region \`${x.data.area}\``,
        },

        exit_region_areas: {
            type: 'exitRegion',
            description: (x) => `vessel exit region \`${x.data.area}\``,
        },

        exit_channel_areas: {
            type: 'exitChannel',
            description: (x) => `vessel enter channel \`${x.data.area}\``,
        },

        enter_channel_areas: {
            type: 'enterChannel',
            description: (x) => `vessel exit channel \`${x.data.area}\``,
        },

        exit_anchorage_areas: {
            type: 'exitAnchorage',
            description: (x) => `vessel enter anchorage \`${x.data.area}\``,
        },

        enter_anchorage_areas: {
            type: 'enterAnchorage',
            description: (x) => `vessel exit anchorage \`${x.data.area}\``,
        },

        exit_reserved_areas: {
            type: 'exitReservedArea',
            description: (x) => `vessel exit reserved area \`${x.data.area}\``,
        },

        enter_reserved_areas: {
            type: 'enterRereservedArea',
            description: (x) => `vessel enter reserved area \`${x.data.area}\``,
        },

        enter_berth_areas: {
            type: 'enterBerth',
            description: (x) => `vessel enter berth \`${x.data.area}\``,
        },

        exit_berth_areas: {
            type: 'exitBerth',
            description: (x) => `vessel exit berth \`${x.data.area}\``,
        },

        start_transit: {
            type: 'startTransit',
            description: () => '',
        },

        end_transit: {
            type: 'endTransit',
            description: () => '',
        },

        vessel_state_change: {
            type: 'vesselStatus',
            description: (x) => `Vessel status update to \`${x.data.state}\``,
        },

        lateral_check: {
            type: 'data',
            algorithm: 'vesselLateralCheck',
            description: (x) => `${x.data.status} lateral. Error: ${x.data.error}`,
        },

        heading_check: {
            type: 'data',
            algorithm: 'vesselHeadingCheck',
            description: (x) => `${x.data.status} heading. Error: ${x.data.error}`,
        },
    };

    return events.reduce<EventType[]>((results, event) => {
        const config = eventConfig[event.type];

        if (!config) {
            return results;
        }

        results.push({
            id: getId(),
            type: config.type,
            description: config.description(event),
            ...(config.algorithm && {
                algorithm: config.algorithm,
            }),
            valid_time_utc: event.time_utc,
            transit_id: transitId,
            port_area_id: null,
            region_area_id: null,
            status: 'active',
            level: 'system',
            port_area: null,
            region_area: null,
        });

        return results;
    }, []);
}

export function formatOffsetTosend(offset: OffsetType) {
    const formatType = (text: string) => {
        if (!text) {
            return text;
        }

        const normalized = text
            .replace(/lateral/gi, '')
            .replace(/heading/gi, '')
            .trim()
            .toLowerCase();

        switch (normalized) {
            case 'manual':
                return 'MANUAL';

            case 'good':
                return 'GOOD';

            case 'bad':
                return 'BAD';

            default:
                return 'UNKNOWN';
        }
    };

    return {
        ...offset,
        lateral: formatType(offset.lateral),
        heading: formatType(offset.heading),
    };
}

export async function formatTransits(
    dateTimeFormat: DateTimeFormat,
    tracks: any,
    switchs: SwitchType,
    port: PortState,
    version: number,
    getData = true
) {
    console.log(version);
    const transits: TransitType[] = [];
    const transitsData: TransitsDataType = {};
    const tags: TagType[] = port.tags;
    const areas = port.port?.port_areas;
    const urls = port.port?.port_urls;
    const vesselCategories = port?.vesselCategories;

    const emptyTrackResult = {
        trackState: false,
        trackData: {},
        trackId: null,
    };

    // ======================================================
    // ======================= VERSION 2 ====================
    // ======================================================
    if (version === 2) {
        for (let index = 0; index < tracks.transits.length; index++) {
            const element = tracks.transits[index];
            //TODO we need the vessel id from the back-end
            const vesselId = getId();

            const offsets: any = {};
            element.offsets.forEach((offset: any) => {
                offsets[offset.id] = formatOffset(offset);
            });

            const bestData = element.positions.best;

            const getDataFile = (element: any, type: string, orderTypes: string[]) => {
                for (let index = 0; index < orderTypes.length; index++) {
                    switch (orderTypes[index]) {
                        case 'arrow':
                            if (element[type]?.arrow_file_url !== null) {
                                return { file: element[type].arrow_file_url, type: orderTypes[index] };
                            }
                            break;
                        case 'json':
                        case 'wkb':
                            if (element[type]?.file_url !== null) {
                                return { file: element[type].file_url, type: orderTypes[index] };
                            }
                            break;
                    }
                }
                return null;
            };

            const fileData = getData
                ? {
                      swept_path: getDataFile(element, 'swept_path', ['wkb']),
                      smoothed: getDataFile(element.positions, 'smoothed', ['arrow', 'json']),
                      centroid: getDataFile(element.positions, 'centroid', ['arrow', 'json']),
                      raw_ais: getDataFile(element.positions, 'raw_ais', ['arrow', 'json']),
                  }
                : {};

            const anlaysisFile = getDataFile(element, 'min_mean_max', ['json']);
            const anlaysisData = anlaysisFile
                ? await downloadDataFile(anlaysisFile.file, 'application/json', 'json')
                : [];

            const { trackState, trackData, trackId } = getData
                ? await loadBestData(
                      dateTimeFormat,
                      bestData,
                      element.transit.transit_id,
                      dateTimeFormat.parseDateTime(element.transit.start_time_utc),
                      dateTimeFormat.parseDateTime(element.transit.end_time_utc),
                      fileData
                  )
                : emptyTrackResult;

            const vesselCategorie = vesselCategories.find((n) => {
                return n.name.toLowerCase() === element.vessel.vessel_type.toLowerCase();
            });
            if (!vesselCategorie) {
                vesselCategories.find((n) => {
                    return n.id === 1;
                });
            }
            transits.push({
                visit_id: element.transit.visit_id,
                uuid: element.transit.transit_id,
                vessel_id: vesselId,
                id: element.transit.transit_id,
                mmsi: parseInt(element.transit.mmsi),
                draft_m: element.vessel.draft_m,
                transit_analysis: anlaysisData.flatMap((s: any) => {
                    const area = areas?.find((n) => n.key === s.region_slug && n.type === 'regionAreas');
                    const url = urls?.find((n) => n.key === s.source_slug);
                    const key = linkKeys(s.key, url?.environmental_type || 'maneuver', true);
                    const tomps = key.includes('kn') && s.key.includes('mps');

                    const base = {
                        port_area: area?.id || null,
                        port_area_id: area?.id || null,
                        port_url: url?.id || null,
                        port_url_id: url?.id || null,
                        type: url?.environmental_type || 'AIS',
                    };

                    if (s.value && typeof s.value === 'object' && !Array.isArray(s.value)) {
                        return Object.entries(s.value).flatMap(([name, value]) => {
                            const convertedValue = tomps ? (value as number) * 0.514444 : value;

                            return [
                                {
                                    ...base,
                                    key: `${name}_${key}`,
                                    value: convertedValue,
                                },
                                ...(name === 'mean' && area?.key === 'tauranga-region-areas-whole-port'
                                    ? [
                                          {
                                              ...base,
                                              key,
                                              value: convertedValue,
                                          },
                                      ]
                                    : []),
                            ];
                        });
                    }

                    return [
                        {
                            ...base,
                            key,
                            value: s.value,
                        },
                    ];
                }),
                transit_tags: element.tag_slugs
                    .map((x: string) => {
                        const tag = tags.find((y) => y.key === x);
                        return tag
                            ? {
                                  transit_id: element.transit.transit_id,
                                  tag_id: tag.id,
                                  tag,
                                  status: 'active',
                              }
                            : undefined;
                    })
                    .filter(Boolean),

                comments: element.comment_ids.map((x) => {
                    return { id: x };
                }),
                events: unifiedEvents(element.events, element.transit.id),
                analysis: anlaysisData.length >= 1,
                environmental: false,
                status: element.transit.transit_state,

                vessel: {
                    ...element.vessel,
                    id: vesselId,
                    mmsi: parseInt(element.vessel.mmsi),
                    vessel_sub_category: vesselCategorie,

                    track: true,
                    name: element.vessel.shipname,

                    vessel_category: vesselCategorie,
                },

                start_utc: dateTimeFormat.parseDateTime(element.transit.start_time_utc),
                end_utc: dateTimeFormat.parseDateTime(element.transit.end_time_utc),
                updated_at: dateTimeFormat.parseDateTime(element.transit.last_updated_time_utc),
                meta_data: fileData,

                options: {
                    display:
                        element.tag_slugs.filter((x: string) => {
                            return x === 'global-admin-for-review';
                        }).length === 0,
                    expand: false,
                    highlight: false,
                    select: true,
                    inspect: false,

                    drawSweptPath: switchs.sweptPath.value,
                    sweptPathLoaded: false,

                    drawTrackLoaded: trackState,
                    drawTrack: switchs.vesselTracks.value,
                    oldDrawTrack: switchs.vesselTracks.value,

                    outline: switchs.vesselOutline.value,
                    oldOutline: switchs.vesselOutline.value,
                },

                best: bestData,
            });

            // @ts-ignore
            transitsData[element.transit.transit_id] = {
                ...trackData,
                offsets,
                [`${bestData}Id`]: trackId,
            };
        }

        return { transits, transitsData };
    }

    // ======================================================
    // ======================= VERSION 1 ====================
    // ======================================================
    for (const trackKey of Object.keys(tracks.items)) {
        for (const transit of tracks.items[trackKey].transits) {
            const { trackState, trackData, trackId } = getData
                ? await getBestData(
                      dateTimeFormat,
                      transit.best,
                      transit.id,
                      dateTimeFormat.parseDateTime(transit.start_utc),
                      dateTimeFormat.parseDateTime(transit.end_utc),
                      tracks.items[trackKey]
                  )
                : emptyTrackResult;

            const uuid = getId();

            // @ts-ignore
            transitsData[uuid] = {
                ...trackData,
                offsets: tracks.items[trackKey].offsets,
                [`${transit.best}Id`]: trackId,
            };

            transits.push({
                uuid,
                id: transit.id,
                mmsi: transit.mmsi,
                transit_tags: transit.transit_tags,
                best: transit.best,
                start_utc: dateTimeFormat.parseDateTime(transit.start_utc),
                end_utc: dateTimeFormat.parseDateTime(transit.end_utc),
                updated_at: dateTimeFormat.parseDateTime(transit.updated_at),
                draft_m: transit.draft_m,
                transit_analysis: transit.transit_analysis,
                visit_id: transit.visit_id,
                status: transit.status,
                events: transit.events,
                comments: transit.comments,
                environmental: Boolean(transit.environmental),
                analysis: Boolean(transit.analysis),
                vessel: tracks.items[trackKey].vessel,
                vessel_id: tracks.items[trackKey].vessel_id,
                //@ts-ignore
                data: {},
                options: {
                    display: transit.type !== 'draft',
                    expand: false,
                    highlight: false,
                    select: transit.type !== 'draft',
                    inspect: false,

                    drawSweptPath: switchs.sweptPath.value,
                    sweptPathLoaded: false,

                    drawTrackLoaded: trackState,
                    drawTrack: switchs.vesselTracks.value,
                    oldDrawTrack: switchs.vesselTracks.value,

                    outline: switchs.vesselOutline.value,
                    oldOutline: switchs.vesselOutline.value,
                },
            });
        }
    }

    return { transits, transitsData };
}

async function loadBestData(
    dateTimeFormat: DateTimeFormat,
    bestData: string,
    transitId: number,
    startTime: DateTimeFormat,
    endTime: DateTimeFormat,
    metaData: any
): Promise<any> {
    if (!metaData || !metaData[bestData]) {
        return { trackState: false, trackId: null, trackData: { [bestData]: [] } };
    }

    let data: any = [];

    if (metaData[bestData].type === 'arrow') {
        const fileData = await downloadDataFile(metaData[bestData].file, 'application/octet-stream', 'arraybuffer');

        const table = tableFromIPC(fileData);

        const buildTransitDataFromArrow = (table: Table, startTime: any, endTime: any, transitId: string | number) => {
            const messageTime = table.getChild('message_time_utc');
            const cog = table.getChild('cog_deg');
            const easting = table.getChild('easting_m');
            const heading = table.getChild('heading_deg');
            const northing = table.getChild('northing_m');
            const sogKn = table.getChild('sog_kn');
            const rot = table.getChild('rot');
            const offsetId = table.getChild('offset_id');
            const draft_m = table.getChild('draft_m');
            const crot_deg_min = table.getChild('crot_deg_min');
            const drift_angle_deg = table.getChild('drift_angle_deg');

            if (!messageTime) return data;

            for (let i = 0; i < table.numRows; i++) {
                const time = messageTime.get(i);
                if (dateTimeFormat.parseDateTime(time).isBetween(startTime.date, endTime.date)) {
                    data.push({
                        id: i,
                        drift_angle_deg: drift_angle_deg?.get(i), // missing
                        crot_deg_min: crot_deg_min?.get(i), // missing
                        cog_deg: cog?.get(i),
                        east_utm: easting?.get(i),
                        heading_deg: heading?.get(i),
                        north_utm: northing?.get(i),
                        draft_m: draft_m?.get(i),
                        sog_mps: (sogKn?.get(i) ?? 0) * 0.514444,
                        rot_deg_min: rot?.get(i),
                        transit_id: transitId,
                        vessel_offset_id: offsetId?.get(i),
                        valid_time_utc: time,
                    });
                }
            }

            return data;
        };

        data = buildTransitDataFromArrow(table, startTime, endTime, transitId);
    } else if (metaData[bestData].type === 'json') {
        const fileData = await downloadDataFile(metaData[bestData].file, 'application/json', 'json');
        for (let index = 0; index < fileData?.message_time_utc.length; index++) {
            if (
                dateTimeFormat.parseDateTime(fileData?.message_time_utc[index]).isBetween(startTime.date, endTime.date)
            ) {
                data.push({
                    id: index,
                    drift_angle_deg: fileData.drift_angle_deg[index], //missing
                    crot_deg_min: fileData.crot_deg_min[index], //missing
                    cog_deg: fileData.cog_deg[index],
                    east_utm: fileData.easting_m[index],
                    heading_deg: fileData.heading_deg[index],
                    north_utm: fileData.northing_m[index],
                    draft_m: fileData.draft_m[index],
                    sog_mps: fileData.sog_kn[index] * 0.514444,
                    rot_deg_min: fileData.rot[index],
                    transit_id: transitId,
                    vessel_offset_id: fileData.offset_id[index],
                    valid_time_utc: fileData?.message_time_utc[index],
                });
            }
        }
    }

    return {
        trackState: false,
        trackId: getId(),
        trackData: { [bestData]: data },
    };
}
function getBestData(
    dateTimeFormat: DateTimeFormat,
    bestData: string,
    transitId: number,
    startTime: DateTimeFormat,
    endTime: DateTimeFormat,
    track: TrackType
) {
    // Ensure the track object and its properties exist to avoid runtime errors
    if (!track || !track[bestData] || !track[bestData].coordinates || !track[bestData].data) {
        return { trackState: false, trackId: null, trackData: { [bestData]: [] } };
    }

    // Safely access the coordinates at the specified index
    const sigmants = track[bestData].coordinates;
    const coordinates: number[][] = [];
    sigmants.forEach((sigmant) => {
        sigmant.forEach((pointIndex) => {
            const point = track[bestData].data[pointIndex];
            if (point && point.transit_id === transitId) {
                if (dateTimeFormat.parseDateTime(point.valid_time_utc).isBetween(startTime.date, endTime.date, true )) {
                    coordinates.push(point);
                }
            }
        });
    });

    if (!coordinates || !Array.isArray(coordinates)) {
        window?.log(`ERROR : Transit ${transitId}has no best data `, 'color: red');
        return { trackState: false, trackData: { [bestData]: [] }, trackId: null };
    }

    return {
        trackState: coordinates.length > 0,
        trackId: getId(),
        trackData: { [bestData]: coordinates },
    };
}

export function formatEvents(events: EventType[]): EventType[] {
    return events
        .filter(
            (event: EventType) =>
                [
                    'enterSwing',
                    'enterChannel',
                    'enterPilotBoardingGround',
                    'exitPilotBoardingGround',
                    'atBerth',
                    'enterBerth',
                    'exitBerth',
                    'enterAnchorage',
                    'exitAnchorage',
                ].includes(event.type) && event.description != null
        )
        .filter((event, index, data) => {
            if ((index == 0 || data.length - 1 == index) && event.description.indexOf('`') >= 0) {
                return true;
            }
            return false;
        })
        .map((event) => {
            return {
                ...event,
                description: formatEventText(event?.description),
                valid_time_utc: event.valid_time_utc,
            };
        });
}

export function formatEventText(text: string): string {
    //@ts-ignore
    return text?.match(new RegExp('`(.*)`'))?.[1] || '';
}

export function formatOffset(offset: any): OffsetType {
    return {
        id: offset.id,
        bow_m: offset.corrected_bow_m,
        stern_m: offset.corrected_stern_m,
        port_m: offset.corrected_port_m,
        stbd_m: offset.corrected_stbd_m,
        lateral_with: offset.correction ? offset.correction.lateral_port_error_m : 0,
        lateral_calculation: offset.correction ? offset.correction.lateral_port_error_m : 0,
        lateral_correction: offset.correction ? offset.correction.lateral_port_applied_m : 0,
        lateral: offset.correction ? offset.correction.lateral_status : 'GOOD',
        forward_correction: offset.correction ? offset.correction.forward_applied_m : 0,
        heading_with: offset.correction ? offset.correction.heading_error_deg : 0,
        heading_calculation: offset.correction ? offset.correction.heading_error_deg : 0,
        heading_correction: offset.correction ? offset.correction.heading_applied_deg : 0,
        heading: offset.correction ? offset.correction.heading_status : 'GOOD',
        type: offset.type === 'raw_ais' ? 'AIS CLASS A' : 'calculated',
    };
}


export function toFixedValue(value: number, decimals = 0) {
    const factor = Math.pow(10, decimals);
    const roundedValue = Math.round(value * factor) / factor;

    return roundedValue % 1 === 0 ? roundedValue.toFixed(decimals) : roundedValue;
}


export function getEnviroDisplayDataAvrages(
    transit,
    environmentalSources,
    enviroDisplayOrders,
    enviroDisplay,
    isUsed = true
) {
    const isUsedItem = (transit, id) => {
        const transitAnalysis = transit.transit_analysis.filter((item) => {
            return item.port_url_id == id;
        });

        return transitAnalysis.length != 0;
    };

    const isUsedValue = (transit, type, key, source) => {
        const transitAnalysis = transit.transit_analysis.find((item) => {
            return item.key == key && item.type == type && item.port_url_id == source;
        });
        if (transitAnalysis == undefined) {
            return false;
        }
        if (transitAnalysis.value == null) {
            return false;
        }
        return true;
    };

    const getFormattedTransitValue = (transit, type, key, source, options) => {
        if (!transit) {
            return undefined;
        }

        const transitAnalysis = transit.transit_analysis.find(
            (item) => item.key === key && item.type === type && item.port_url_id === source
        );

        if (!transitAnalysis) {
            return undefined;
        }

        if (transitAnalysis.value == null) {
            return null;
        }

        const value = transitAnalysis.value * options.database2display_unit_factor;
        const decimals = options.decimal || 0;


        return toFixedValue(value, decimals);
    };

    return enviroDisplayOrders.map((enviroDisplayOrder) => {
        return {
            key: enviroDisplayOrder.key,

            instruments: environmentalSources
                .filter((environmentalSource) => {
                    if (!isUsed) {
                        return environmentalSource.environmental_type == enviroDisplayOrder.key;
                    }
                    return (
                        environmentalSource.environmental_type == enviroDisplayOrder.key &&
                        isUsedItem(transit, environmentalSource.id)
                    );
                })
                .sort((a, b) => a.order - b.order)
                .map((environmentalSource) => {
                    const values: any = {
                        id: environmentalSource.id,
                        uuid: environmentalSource.uuid,
                        description: environmentalSource.description,
                        order: environmentalSource.order,
                        slug: environmentalSource.key,
                        values: enviroDisplayOrder.list
                            .filter((enviroDisplayOrderkey) => {
                                return isUsedValue(
                                    transit,
                                    enviroDisplayOrder.key,
                                    enviroDisplayOrderkey,
                                    environmentalSource.id
                                );
                            })
                            .map((enviroDisplayOrderkey, enviroDisplayOrderindex) => {
                                return {
                                    index: enviroDisplayOrderindex,
                                    name: enviroDisplay[enviroDisplayOrder.key][enviroDisplayOrderkey].name,
                                    key: enviroDisplayOrderkey,
                                    value: getFormattedTransitValue(
                                        transit,
                                        enviroDisplayOrder.key,
                                        enviroDisplayOrderkey,
                                        environmentalSource.id,
                                        enviroDisplay[enviroDisplayOrder.key][enviroDisplayOrderkey]
                                    ),
                                    unit: enviroDisplay[enviroDisplayOrder.key][enviroDisplayOrderkey].unit,
                                };
                            }),
                    };

                    return values;
                }),
        };
    });
}

export function unifiedSelectedPlan(pilotagePlans) {
    pilotagePlans = pilotagePlans ? pilotagePlans : { plans: [] };

    pilotagePlans?.plans.map((pilotagePlan) => {
        pilotagePlan.group = [
            ...(pilotagePlan?.noGoAreas || []),
            ...(pilotagePlan?.safetyMargins || []),
            ...(pilotagePlan?.markerPoints || []),
            pilotagePlan.route,
            pilotagePlan.XTD,
        ];
        return pilotagePlan;
    });
}
